import { useEffect, useRef } from 'react'

const PARTICLE_COUNT = 90
// Roughly matches the hero's own footprint (see HomePage's max-w-[900px] header) so the
// field reads as "behind the content," not a random box. Slightly wider than the particle
// count alone would suggest, so a sparser field still reads as spread out, not clumped.
const SPREAD = { x: 520, y: 260, z: 280 }

/**
 * Ambient WebGL particle field for the homepage hero — a sparse cloud of soft dots
 * drifting slowly behind the headline. Purely decorative (aria-hidden, pointer-events
 * disabled) and purely additive: three.js loads via a dynamic import so it never adds
 * to the initial bundle other pages pay for, and any failure (WebGL disabled/unsupported,
 * the import failing) is caught and swallowed — the CSS glow already carries the hero
 * on its own, this only enhances it when available. Respects prefers-reduced-motion by
 * not attempting to load or render anything at all.
 */
export function HeroParticleField() {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const container = containerRef.current
    if (!container) return

    let cancelled = false
    let teardown: (() => void) | undefined

    import('three')
      .then((THREE) => {
        if (cancelled) return
        teardown = mountScene(THREE, container)
      })
      .catch(() => {
        // No WebGL, three.js failed to load, or something else went wrong — nothing to
        // do here. The hero still works, just without this extra layer.
      })

    return () => {
      cancelled = true
      teardown?.()
    }
  }, [])

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      // No explicit z-index: rendered first in the header, so plain DOM/paint order
      // already puts it behind every sibling that follows (the glow, the hero content) —
      // avoids a negative z-index risking getting trapped behind an ancestor's own
      // background instead of just the intended siblings.
      className="pointer-events-none absolute inset-0 overflow-hidden"
    />
  )
}

/** A soft, feathered circular dot — plain PointsMaterial with no map renders hard-edged
 *  squares, so this is the standard bridge for a rounder, softer look. */
function makeSoftDotTexture(THREE: typeof import('three')) {
  const size = 64
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
  gradient.addColorStop(0, 'rgba(255,255,255,1)')
  gradient.addColorStop(0.5, 'rgba(255,255,255,0.55)')
  gradient.addColorStop(1, 'rgba(255,255,255,0)')
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, size, size)
  const texture = new THREE.CanvasTexture(canvas)
  texture.needsUpdate = true
  return texture
}

function mountScene(
  THREE: typeof import('three'),
  container: HTMLElement
): (() => void) | undefined {
  const canvas = document.createElement('canvas')
  container.appendChild(canvas)

  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true })
  const scene = new THREE.Scene()
  const camera = new THREE.PerspectiveCamera(55, 1, 1, 1500)
  camera.position.z = 520

  // The homepage always forces the light palette (.fl-force-light) — reading the token
  // at runtime instead of hardcoding a hex keeps this in sync with the design system
  // rather than duplicating a color literal here.
  const primary = getComputedStyle(container).getPropertyValue('--fl-primary').trim() || '#2d7a5f'

  const positions = new Float32Array(PARTICLE_COUNT * 3)
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    positions[i * 3] = (Math.random() - 0.5) * SPREAD.x * 2
    positions[i * 3 + 1] = (Math.random() - 0.5) * SPREAD.y * 2
    positions[i * 3 + 2] = (Math.random() - 0.5) * SPREAD.z * 2
  }
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))

  const material = new THREE.PointsMaterial({
    color: new THREE.Color(primary),
    map: makeSoftDotTexture(THREE),
    // Fixed screen-space size (not world-space) — sizeAttenuation would otherwise shrink
    // particles based on their distance from the camera, making the actual on-screen size
    // unpredictable without visually tuning it against a real render.
    size: 4.5,
    sizeAttenuation: false,
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
  })

  const points = new THREE.Points(geometry, material)
  scene.add(points)

  function resize() {
    const { width, height } = container.getBoundingClientRect()
    if (width === 0 || height === 0) return
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setSize(width, height)
    camera.aspect = width / height
    camera.updateProjectionMatrix()
  }
  resize()

  const resizeObserver = new ResizeObserver(resize)
  resizeObserver.observe(container)

  let frameId: number
  function animate() {
    // Barely-there drift — this is ambient texture, not something meant to draw the eye.
    points.rotation.y += 0.0003
    points.rotation.x += 0.0001
    renderer.render(scene, camera)
    frameId = requestAnimationFrame(animate)
  }
  frameId = requestAnimationFrame(animate)

  return () => {
    cancelAnimationFrame(frameId)
    resizeObserver.disconnect()
    geometry.dispose()
    material.map?.dispose()
    material.dispose()
    renderer.dispose()
    canvas.remove()
  }
}
